/** TanStack Query hooks — the single place server state is fetched/cached. */
import { useQuery } from '@tanstack/react-query'
import { api } from './client'
import { config, isV1Configured } from '../config/env'
import { fetchPosCustomer, validatePos, type ValidateResult } from './v2/client'
import type { V2CustomerResult } from './v2/types'

export function useValidate() {
  return useQuery({
    queryKey: ['validate', config.webextUuid],
    queryFn: () => api.validate(config.webextUuid),
    enabled: isV1Configured(),
    retry: 1,
  })
}

export function useCustomer(qr: string | null) {
  return useQuery({
    queryKey: ['customer', config.webextUuid, qr],
    queryFn: () => api.customer(config.webextUuid, qr as string),
    enabled: Boolean(qr) && isV1Configured(),
    retry: 1,
  })
}


/** DEV-378: the V2 boot probe.
 *
 * Replaces `useValidate` as the thing the app waits for at startup. The V1 hook is
 * still here because the V1 endpoints stay callable during the migration, but it now
 * only runs when V1 is actually configured — enabling it without a `webext_uuid` was
 * what produced "missing webext_uuid" on a till that is correctly set up for V2.
 */
export function useV2Validate() {
  return useQuery<ValidateResult>({
    queryKey: ['v2', 'validate', config.posSurfaceUrl],
    queryFn: validatePos,
    enabled: Boolean(config.posSurfaceUrl),
    // One retry: a till that has just woken up often loses the first request, and
    // retrying forever would keep a broken configuration looking merely slow.
    retry: 1,
  })
}


/** DEV-378: the scanned customer, from the V2 surface.
 *
 * Replaces `useCustomer`, which called V1 with a `webext_uuid` that no longer exists.
 * That mismatch is why a scan did nothing: the query was correctly disabled, so the
 * flow advanced to a screen with no data behind it.
 */
export function useV2Customer(consumerRef: string | null, locationId?: number) {
  return useQuery<V2CustomerResult>({
    queryKey: ['v2', 'customer', consumerRef, locationId ?? null],
    queryFn: () => fetchPosCustomer(consumerRef as string, locationId),
    enabled: Boolean(consumerRef) && Boolean(config.posSurfaceUrl),
    retry: 1,
  })
}
